import { Component, Input, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  ShoppingCartIcon,
  StarIcon,
} from 'lucide-angular';
import type { Producto } from '../../../types/product.types';
import { CartService } from '../../../services/cart.service';
import { NotificationService } from '../../../services/notification.service';
import { smartTitle } from '../../../utils/pipes/smart-capitalize';
import { VehicleCompatibilityComponent } from '../product-card/vehicle-compatibility.component';
import { DeliveryTypeByStore } from '../delivery-type-by-store/delivery-type-by-store';
import { WarrantyGuard } from '../warranty-guard/warranty-guard';
import { CoreDeposit } from '../core-deposit/core-deposit';
import { ProductPrice } from '../price/price';

@Component({
  selector: 'allas-product-list-item',

  imports: [
    CommonModule,
    LucideAngularModule,
    VehicleCompatibilityComponent,
    DeliveryTypeByStore,
    WarrantyGuard,
    CoreDeposit,
    ProductPrice,
  ],
  template: `
    <article
      class="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-all duration-200 flex flex-col md:flex-row gap-4"
    >
      <!-- Product Image Section -->
      <ng-container [ngTemplateOutlet]="productImage"></ng-container>

      <!-- Product Details Section -->
      <section class="flex-1 space-y-3">
        <!-- Product Title -->
        <a
          [href]="'/productos/' + product.slug"
          class="hover:text-appPrimary-600 transition-colors"
        >
          <h2 class="font-semibold text-gray-900 text-base leading-tight">
            {{ product.nombre_web }}
          </h2>
        </a>

        <!-- Product Identification -->
        <ng-container [ngTemplateOutlet]="productIdentification"></ng-container>

        <!-- Warranty Information -->
        <ng-container [ngTemplateOutlet]="warrantyInfo"></ng-container>

        <!-- Product Rating -->
        <!-- <ng-container [ngTemplateOutlet]="productRating"></ng-container> -->

        <!-- Vehicle Compatibility -->
        <allas-vehicle-compatibility
          [product]="product"
          [status]="compatibleWithVehicle ? 'compatible' : 'not-selected'"
          [defaultVehicle]="defaultVehicleAlias"
        ></allas-vehicle-compatibility>

        <!-- Product Attributes -->
        <ng-container [ngTemplateOutlet]="productAttributes"></ng-container>

        <!-- Product Description -->
        <ng-container [ngTemplateOutlet]="productDescription"></ng-container>
      </section>

      <!-- Pricing and Actions Section -->
      <aside
        class="flex-shrink-0 flex flex-col items-end justify-between space-y-4 w-3/12"
      >
        <!-- Pricing Information -->
        <div class="flex flex-col gap-0 w-full">
          <allas-product-price [price]="product.precio"></allas-product-price>
          @if (product.precio > 0) {
          <ng-container [ngTemplateOutlet]="coreDeposit"></ng-container>
          }
        </div>

        <!-- Actions -->
        @if (product.precio > 0) {
        <div class="flex flex-col gap-2">
          <allas-delivery-type-by-store
            [product]="product"
          ></allas-delivery-type-by-store>
          <ng-container [ngTemplateOutlet]="addToCartButton"></ng-container>
        </div>
        }
      </aside>
    </article>

    <!-- Templates -->
    <ng-template #productImage>
      <div class="flex-shrink-0 w-4/12">
        <div class="w-full h-full bg-gray-50 rounded-lg overflow-hidden">
          <img
            [src]="product.imagenes.thumb[0] || '/placeholder.svg'"
            [alt]="product.nombre_web + ' - ' + product.descripcion"
            class="w-full h-full object-contain"
            loading="lazy"
            width="200"
            height="200"
          />
        </div>
      </div>
    </ng-template>

    <ng-template #productIdentification>
      <p class="text-xs text-gray-500">
        Parte #{{ product.part_number }}
        <span class="text-gray-300 text-[8px]" aria-hidden="true">|</span>
        Código #{{ product.sku.trim() }}
      </p>
    </ng-template>

    <ng-template #warrantyInfo>
      @if (warrantyYears() > 0) {
      <allas-warranty-guard
        [months]="warrantyYears() * 12"
        styleClass="mt-2"
      ></allas-warranty-guard>
      }
    </ng-template>

    <ng-template #productRating>
      <div
        class="flex items-center gap-1"
        role="img"
        [attr.aria-label]="
          'Calificación de' + product.rating + ' estrellas de 5'
        "
      >
        @for (item of [1,2,3,4,5]; track $index) {
        <lucide-icon
          [name]="starIcon"
          class="w-4 h-4"
          [ngClass]="{
            'text-gray-300': item > product.rating,
            'text-yellow-500': item <= product.rating
          }"
          [attr.aria-hidden]="true"
        />
        }
        <span class="text-xs">{{ product.rating }}</span>
        <span class="text-xs underline">({{ product.votes }})</span>
      </div>
    </ng-template>

    <ng-template #productAttributes>
      <div class="flex flex-wrap gap-2">
        @for (attribute of attributesToDisplay; track $index) {
        <span
          class="rounded-lg px-3 py-1 text-xs transition-colors duration-200 bg-gray-100 text-gray-800"
        >
          <strong>{{ attribute.label }}:</strong> {{ attribute.value }}
        </span>
        }
      </div>
    </ng-template>

    <ng-template #productDescription>
      @if (product.descripcion) {
      <div class="text-xs text-gray-500">
        <!-- <span class="font-bold">Nota:</span> -->
        <span>{{ product.descripcion }}</span>
      </div>
      }
    </ng-template>

    <ng-template #coreDeposit>
      @if (product.subcategoria === 'bateria') {
      <allas-core-deposit [product]="product"></allas-core-deposit>
      }
    </ng-template>

    <ng-template #addToCartButton>
      <button
        (click)="addToCart()"
        class="w-full md:w-auto bg-appPrimary-500 hover:bg-appPrimary-400 text-white py-2 px-4 rounded font-semibold text-sm transition-colors flex items-center justify-center space-x-2"
      >
        <lucide-icon
          [name]="shoppingCartIcon"
          class="w-4 h-4"
          aria-hidden="true"
        ></lucide-icon>
        <span>Agregar al carrito</span>
      </button>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductListItem {
  @Input() product!: Producto;
  @Input() compatibleWithVehicle = false;
  @Input() defaultVehicleAlias = '';

  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly starIcon = StarIcon;
  readonly warrantyYears = computed(() => {
    return this.getWarrantyYears(this.product.marca);
  });

  addToCart(): void {
    this.cartService.addItemToCart({
      producto: this.product,
      cantidad: 1,
    });
    this.notificationService.showSuccess(
      `${this.product.nombre_web} agregado al carrito`,
      'Producto disponible para cotización'
    );
  }

  get attributesToDisplay() {
    return this.product.atributos.filter(
      (attribute) => attribute.filterable && !attribute.is_from_api_catalogo
    );
  }

  getIntFormattedPrice(): string {
    // Sin redondear a nada, solo quitar los decimales, agregar las comas para separar los miles
    const intPrice = Number(this.product.precio.toString().split('.')[0]);
    return intPrice.toLocaleString('es-CO').replace(/\./g, ',');
  }

  getOnlyDecimalPrice(): string {
    const decimal = this.product.precio.toString().split('.')[1] ?? '00';
    return decimal.length === 1 ? `0${decimal}` : decimal;
  }

  getWarrantyYears(brand: string): number {
    switch (brand) {
      case 'TAB POLAR':
        return 3;
      case 'TAB 5G':
        return 4;
      case 'TAB AGM STOP & GO':
        return 5;
      default:
        return 0;
    }
  }
}
